import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Textarea from '../../../components/ui/Textarea';
import Button from '../../../components/ui/Button';
import CamperPreview from './CamperPreview';

export default function CabinCallEditor({ data, onSave, onHistory }) {
  const [form, setForm] = useState(data || {});
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => setForm(data || {}), [data]);

  const handleChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const getAcademyPrompts = () => {
    try {
      const parsed = JSON.parse(form.compass_prompts || '[]');
      if (!Array.isArray(parsed)) return [];

      return parsed.map(item => {
        if (typeof item === 'string') {
          return {
            title: '',
            description: '',
            prompt: item,
            field_type: 'textarea'
          };
        }

        return {
          title: item?.title || '',
          description: item?.description || item?.desc || '',
          prompt: item?.prompt || item?.text || '',
          field_type: item?.field_type === 'text' ? 'text' : 'textarea'
        };
      });
    } catch {
      return [];
    }
  };

  const updateAcademyPrompts = (prompts) => {
    handleChange('compass_prompts', JSON.stringify(prompts));
  };

  const addAcademyPrompt = () => {
    updateAcademyPrompts([
      ...getAcademyPrompts(),
      {
        title: '',
        description: '',
        prompt: '',
        field_type: 'textarea'
      }
    ]);
  };

  const updateAcademyPrompt = (index, key, value) => {
    const prompts = getAcademyPrompts();
    prompts[index] = { ...prompts[index], [key]: value };
    updateAcademyPrompts(prompts);
  };

  const removeAcademyPrompt = (index) => {
    updateAcademyPrompts(
      getAcademyPrompts().filter((_, promptIndex) => promptIndex !== index)
    );
  };

  const academyPrompts = getAcademyPrompts();

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <div className="flex-1 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Input 
            label="Day title" 
            value={form.day_title || ''} 
            onChange={e => handleChange('day_title', e.target.value)} 
            placeholder="e.g. Mindset & Calling"
          />
          <Input 
            label="Persona label" 
            value={form.persona_label || ''} 
            onChange={e => handleChange('persona_label', e.target.value)} 
            placeholder="e.g. The Christian"
          />
        </div>

        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <Input 
              label="Section title" 
              value={form.section_title || ''} 
              onChange={e => handleChange('section_title', e.target.value)} 
            />
          </div>
          
          <button 
            type="button"
            className="flex items-center gap-3 cursor-pointer p-2 bg-gray-50 border rounded-md hover:bg-gray-100 mb-0.5"
            onClick={() => handleChange('is_active', form.is_active ? 0 : 1)}
          >
            <div className={`w-10 h-6 rounded-full relative transition-colors ${form.is_active ? 'bg-green-500' : 'bg-gray-300'}`}>
              <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${form.is_active ? 'left-5' : 'left-1'}`}></div>
            </div>
            <span className="font-bold text-gray-700">Active</span>
          </button>
        </div>

        <Textarea 
          label="Opening paragraph" 
          value={form.opening_paragraph || ''} 
          onChange={e => handleChange('opening_paragraph', e.target.value)} 
          className="min-h-[80px]"
        />

        <div className="space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-bold text-dark-green">Academy Prompts</h3>
              <p className="text-sm text-muted">
                Add individual prompts for students to complete in Study Hall.
              </p>
            </div>
            <Button type="button" variant="outline" onClick={addAcademyPrompt}>
              Add Academy Prompt
            </Button>
          </div>

          {academyPrompts.length === 0 && (
            <p className="rounded-md border border-dashed border-gray-300 bg-white p-4 text-center text-sm text-muted">
              No Academy Prompts have been added.
            </p>
          )}

          {academyPrompts.map((prompt, index) => (
            <div
              key={`academy-prompt-${index}`}
              className="space-y-3 rounded-md border border-gray-200 bg-white p-4"
            >
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-dark-green">
                  Academy Prompt {index + 1}
                </h4>
                <button
                  type="button"
                  onClick={() => removeAcademyPrompt(index)}
                  className="text-sm font-bold text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>

              <Input
                label="Prompt title"
                value={prompt.title}
                onChange={e => updateAcademyPrompt(index, 'title', e.target.value)}
              />

              <Textarea
                label="Prompt description"
                value={prompt.description}
                onChange={e => updateAcademyPrompt(index, 'description', e.target.value)}
                className="min-h-[80px]"
              />

              <Textarea
                label="Prompt question"
                value={prompt.prompt}
                onChange={e => updateAcademyPrompt(index, 'prompt', e.target.value)}
                className="min-h-[100px]"
              />

              <label className="block text-sm font-bold text-gray-700">
                Student response field
                <select
                  value={prompt.field_type}
                  onChange={e => updateAcademyPrompt(index, 'field_type', e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-normal"
                >
                  <option value="textarea">Long response</option>
                  <option value="text">Short response</option>
                </select>
              </label>
            </div>
          ))}
        </div>

        <Textarea 
          label="Compass Prompts (JSON)" 
          value={form.compass_prompts || '[]'} 
          onChange={e => handleChange('compass_prompts', e.target.value)} 
          className="min-h-[150px] font-mono text-sm"
        />

        <div className="grid md:grid-cols-2 gap-4">
          <Input 
            type="datetime-local" 
            label="Unlock Time" 
            value={form.unlock_time || ''} 
            onChange={e => handleChange('unlock_time', e.target.value)} 
          />
          <Input 
            type="datetime-local" 
            label="Close Time" 
            value={form.close_time || ''} 
            onChange={e => handleChange('close_time', e.target.value)} 
          />
        </div>

        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button onClick={() => onSave(form)}>Save Study Hall</Button>
          <Button variant="ghost" onClick={onHistory}>View History</Button>
        </div>
      </div>

      <div className={`w-full lg:w-[400px] ${showPreview ? 'block' : 'hidden lg:block'}`}>
        <CamperPreview type="cabincall" data={form} />
      </div>

      <button 
        className="lg:hidden w-full py-2 bg-gray-100 text-sm font-bold text-gray-600 rounded mt-4" 
        onClick={() => setShowPreview(!showPreview)}
      >
        {showPreview ? 'Hide Preview' : 'Show Preview'}
      </button>
    </div>
  );
}